import { c as _c } from "react/compiler-runtime";
import { CircularProgress, ToggleGroup } from "@flat-ui";
import { Box } from "@mui-wrapper";
import { fetchingUserSettingSelector, userSettingsSelector } from "@store/settings/settings-selector";
import { updateUserSetting } from "@store/settings/settings-thunks";
import { useAppDispatch, useAppSelector } from "@store/store-helper";
import { loggedInUserSelector } from "@store/user/user-selector";
import { useEffect, useState } from "react";

/** Available theme mode options shown in the toggle */
const THEME_MODE_ITEMS = [{
  label: "Light",
  value: "light"
}, {
  label: "Dark",
  value: "dark"
}, {
  label: "System",
  value: "system"
}];

/**
 * Theme mode toggle allowing the user to choose between light, dark, and system themes.
 *
 * The selected value is persisted as the user's `theme_mode` setting and applied across the app.
 * Where the toggle is offered is up to its caller: the account settings page shows it only with
 * beta testing enabled, while the dev tools always do.
 */
export function ThemeModeToggle() {
  const $ = _c(21);
  const dispatch = useAppDispatch();
  const loggedInUser = useAppSelector(loggedInUserSelector);
  const {
    theme_mode: themeModeSetting
  } = useAppSelector(userSettingsSelector);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = fetchingUserSettingSelector("theme_mode");
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const fetchingThemeModeSettings = useAppSelector(t0);
  const savedThemeMode = themeModeSetting?.value ?? "system";
  const [selectedThemeMode, setSelectedThemeMode] = useState(savedThemeMode);
  let t1;
  let t2;
  if ($[1] !== savedThemeMode) {
    t1 = () => {
      setSelectedThemeMode(savedThemeMode);
    };
    t2 = [savedThemeMode];
    $[1] = savedThemeMode;
    $[2] = t1;
    $[3] = t2;
  } else {
    t1 = $[2];
    t2 = $[3];
  }
  useEffect(t1, t2);
  let t3;
  if ($[4] !== dispatch || $[5] !== loggedInUser || $[6] !== themeModeSetting?.modifiedAt) {
    t3 = async function handleThemeChange(newThemeMode) {
      if (!loggedInUser) {
        return;
      }
      setSelectedThemeMode(newThemeMode);
      await dispatch(updateUserSetting({
        identifier: "theme_mode",
        userId: loggedInUser.id,
        payload: {
          value: newThemeMode,
          modifiedAt: themeModeSetting?.modifiedAt ?? ""
        }
      })).unwrap();
    };
    $[4] = dispatch;
    $[5] = loggedInUser;
    $[6] = themeModeSetting?.modifiedAt;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const handleThemeChange = t3;
  let t4;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      position: "relative",
      display: "inline-flex"
    };
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const t5 = fetchingThemeModeSettings === "pending";
  const t6 = fetchingThemeModeSettings === "pending" ? "hidden" : "visible";
  let t7;
  if ($[9] !== t6) {
    t7 = {
      visibility: t6
    };
    $[9] = t6;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  let t8;
  if ($[11] !== handleThemeChange || $[12] !== selectedThemeMode || $[13] !== t5 || $[14] !== t7) {
    t8 = <ToggleGroup items={THEME_MODE_ITEMS} value={selectedThemeMode} onChange={handleThemeChange} isDisabled={t5} sxGroup={t7} />;
    $[11] = handleThemeChange;
    $[12] = selectedThemeMode;
    $[13] = t5;
    $[14] = t7;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  let t9;
  if ($[16] !== fetchingThemeModeSettings) {
    t9 = fetchingThemeModeSettings === "pending" && <CircularProgress size="1.25rem" sx={{
      position: "absolute",
      top: "50%",
      left: "50%",
      transform: "translate(-50%, -50%)"
    }} />;
    $[16] = fetchingThemeModeSettings;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  let t10;
  if ($[18] !== t8 || $[19] !== t9) {
    t10 = <Box sx={t4}>{t8}{t9}</Box>;
    $[18] = t8;
    $[19] = t9;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  return t10;
}